import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ViewChild, TemplateRef } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DOCUMENTS_ACTIONS } from './alldocuments-actions.data';
import { DocumentsAction } from './alldocuments-actions.model';
import { DocumentType } from '../alldocuments-types/alldocuments-types.model';
import { DOCUMENT_TYPES } from '../alldocuments-types/alldocuments-types.data';

@Component({
  selector: 'app-alldocuments-actions',
  templateUrl: './alldocuments-actions.component.html',
  styleUrls: ['./alldocuments-actions.component.scss']
})
export class AlldocumentsActionsComponent implements OnInit, OnChanges {
  @Input() activeFiltersCount: string | number = 0;
  @Input() showResetColumnsButton: boolean = false;
  @Input() isPdfExporting: boolean = false;
  @Input() allowedInvoiceTypes: number[] = [];
  @Input() companies: any[] = [];
  @Input() canCreateDocument: boolean = true;

  curentLang: string = localStorage.getItem('curentLang') || 'en';

  @Output() clearMessages = new EventEmitter<void>();
  @Output() newDocument = new EventEmitter<void>();
  @Output() exportPdf = new EventEmitter<void>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() resetColumns = new EventEmitter<void>();
  @Output() navigateToNewDocument = new EventEmitter<DocumentType>();

  @ViewChild('newDocumentModal') newDocumentModalTemplate!: TemplateRef<any>;

  documentsActions: DocumentsAction[] = [];
  mobileDocumentsActions: MenuItem[] = [];
  documentTypes: DocumentType[] = [];
  documentTypesMenu: MenuItem[] = [];
  
  // Dialog visibility control for mobile
  displayDocumentTypeDialog: boolean = false;


  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private router: Router
  ) {
    // Initialize document types with translation preparation
    this.documentTypes = DOCUMENT_TYPES.map(docType => {
      // Make sure we keep the original data
      return { ...docType };
    });
  }

  ngOnInit(): void {
    // Initialize actions from static data
    this.documentsActions = [...DOCUMENTS_ACTIONS];
    // this.documentTypes = [...DOCUMENT_TYPES];

    // Filter out hidden actions
    this.documentsActions = this.documentsActions.filter(action => action.visible !== false);

    // Initialize document types and apply filtering based on header company
    this.filterDocumentTypes();

    // Create mobile speed dial actions
    this.buildMobileActions();
    
    // Listen for company changes from header cart component
    window.addEventListener('companyChanged', (event: any) => {
      const companyId = event.detail.companyId;
      
      // Re-filter document types when company changes
      this.filterDocumentTypes();
    });
    
    // Subscribe to language changes to rebuild menus when language changes
    this.translate.onLangChange.subscribe(() => {
      this.curentLang = localStorage.getItem('curentLang') || this.translate.currentLang || 'en';
      this.buildDocumentTypesMenu();
      this.buildMobileActions();
    });
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isPdfExporting || changes.canCreateDocument) {
      this.buildMobileActions();
    }
    if (changes.allowedInvoiceTypes) {
      this.filterDocumentTypes();
    }
  }

  private filterDocumentTypes(): void {
    // Use allowedInvoiceTypes from parent component (synced with desktop filtering)
    if (this.allowedInvoiceTypes && this.allowedInvoiceTypes.length > 0) {
      // Filter document types based on allowed invoice types
      this.documentTypes = DOCUMENT_TYPES.filter(docType => 
        this.allowedInvoiceTypes.includes(parseInt(docType.code, 10))
      );
    } else {
      // Fallback: show all types if no allowed types specified
      this.documentTypes = DOCUMENT_TYPES.filter(docType => docType.code !== '430');
    }
    
    // Rebuild the menu with filtered types
    this.buildDocumentTypesMenu();
  }

  private buildDocumentTypesMenu(): void {
    this.documentTypesMenu = this.documentTypes.map(docType => ({
      label: this.translate.instant(docType.label),
      icon: docType.icon,
      routerLink: docType.routerLink,
      command: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('allDocuments.actions.newDocument'),
          detail: `${this.translate.instant('allDocuments.actions.creatingNew')} ${this.translate.instant(docType.label)}`,
          sticky: false,
          life: 10000
        });
      }
    }));
  }

  private buildMobileActions(): void {
    const upgradePlanLabel =
      this.curentLang === 'ar'
        ? 'ترقية الخطة'
        : this.curentLang === 'he'
          ? 'שדרג תוכנית'
          : 'Upgrade plan';

    this.mobileDocumentsActions = [
      ...(this.canCreateDocument
        ? [{
            label: this.translate.instant('allDocuments.actions.newDocument'),
            icon: 'pi pi-file',
            command: () => this.showDocumentTypeDialog()
          }]
        : [{
            label: upgradePlanLabel,
            icon: 'pi pi-arrow-up-right',
            command: () => this.router.navigate(['/dashboard/invoice-subscriptions'])
          }]),
      {
        label: this.isPdfExporting 
          ? this.translate.instant('allDocuments.actions.exportingPdf')
          : this.translate.instant('allDocuments.actions.exportPdf'),
        icon: this.isPdfExporting ? 'pi pi-spin pi-spinner' : 'pi pi-download',
        command: () => this.onExportPdf(),
        disabled: this.isPdfExporting
      },
      {
        label: this.translate.instant('allDocuments.actions.clearFilters'),
        icon: 'pi pi-filter-slash',
        command: () => this.onClearFilters()
      },
      {
        label: this.translate.instant('allDocuments.actions.resetColumns'),
        icon: 'pi pi-sort-alt-slash',
        command: () => this.onResetColumns()
      }
    ];
  }

  // Handler methods that emit events up to the parent component
  onClearMessages(): void {
    this.clearMessages.emit();
  }

  onNewDocument(): void {
    // This method is no longer needed as we use the direct dropdown toggle
    // The filtering is handled automatically in ngOnInit and ngOnChanges
  }

  /**
   * Show document type selection dialog for mobile
   */
  showDocumentTypeDialog(): void {
    // Ensure document types are filtered before showing dialog
    this.filterDocumentTypes();
    this.displayDocumentTypeDialog = true;
  }

  /**
   * Close document type selection dialog
   */
  closeDocumentTypeDialog(): void {
    this.displayDocumentTypeDialog = false;
  }

  /**
   * Handle document type selection and navigate to the corresponding route
   */
  selectDocumentType(docType: DocumentType): void {
    // Close the dialog
    this.closeDocumentTypeDialog();
    
    // Navigate to the specific route for this document type if available
    if (docType && docType.routerLink) {
      this.router.navigate([docType.routerLink]);
    }

    // Show success message
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('allDocuments.actions.newDocument'),
      detail: `${this.translate.instant('allDocuments.actions.creatingNew')} ${this.translate.instant(docType.label)}`,
      sticky: false,
      life: 10000
    });

    // Emit the event for the parent component to handle
    this.navigateToNewDocument.emit(docType);
  }

  onExportPdf(): void {
    this.exportPdf.emit();
  }

  onClearFilters(): void {
    this.clearFilters.emit();
  }

  onResetColumns(): void {
    this.resetColumns.emit();
  }



}
